import React, { useState, useMemo, useEffect } from "react";
import { Spin } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { notifyUser } from "../../../util/util";
import { extractErrorMessage } from '../../../hooks/extractErrorMessage';
import { authApi } from "../../../services/auth.service";
import { ReactComponent as LockIcon } from '../../../assets/UiElements/lock.svg';

export default function PhoneOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const phone = location.state?.phone || "";
  const [otpRef, setOtpRef] = useState(location.state?.otpRef || ""); // otp_ref from API response, can be updated after resend
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(165);
  const [shake, setShake] = useState(false);

  const formatTime = useMemo(() => (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }, []);
  const remainingTime = useMemo(() => seconds > 45 ? formatTime(seconds - 45) : null, [seconds, formatTime]);

  useEffect(() => {
    if (seconds <= 0) return;
    const timer = setInterval(() => setSeconds((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [seconds]);

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      notifyUser('error', 'Please enter the 6-digit OTP received to proceed!', 'Validation Error');
      return;
    }
    setLoading(true);
    try {
      // Use otp_ref if available, otherwise use phone
      const payload = otpRef ? { otp_ref: otpRef, otp } : { phone, otp };
      await authApi.verifyOtp(payload);
      notifyUser("success", "Phone verified! You can now log in.", "Success");
      navigate("/onboard/login");
    } catch (e) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      notifyUser('error', extractErrorMessage(e, 'OTP verification failed'), 'Error');
    }
    setLoading(false);
  };

  const resendOTP = async () => {
    if (seconds > 45) return;
    setShake(true);
    setSeconds(165);
    setTimeout(() => setShake(false), 500);
    try {
      // Use otp_ref if available, otherwise use phone
      const identifier = otpRef || phone;
      const response = await authApi.resendOtp(identifier);
      // Update otp_ref from response if available (new otp_ref for subsequent calls)
      const newOtpRef = response.data?.data?.otp_ref;
      if (newOtpRef) {
        setOtpRef(newOtpRef);
      }
      notifyUser("success", response.data?.message || "OTP has been resent successfully!", "Success");
    } catch (e) {
      notifyUser('error', extractErrorMessage(e, 'Failed to resend OTP'), 'Error');
    }
  };

  const changeHandler = (e) => setOtp(e.target.value);

  return (
    <div className='mx-auto mobile-full-width shadow bg-white p-5 rounded login-width'>
      <div align='center'>
        <p className='lead fw-bold'>Enter your One Time Pin</p>
        <small className="text-center">
          Please enter the 6-digit OTP sent to your registered phone number
        </small>
        <Spin spinning={loading}>
          <div className="mt-4 w-75 mx-auto mobile-full-width">
            <omk-otp-field
              number-of-inputs="6"
              resend-text=""
              resend-button-label=""
              label="One-time Password"
              className={`mb-1 ${shake ? "shake" : ""}`}
              value={otp}
              onInput={changeHandler}
            />
            <div className="d-flex justify-content-center align-items-center mb-3">
              <p className="text-center omk-text-small text-black-100 mb-1">
                Didn't receive the OTP?&nbsp;
              </p>
              <div className="text-center">
                {remainingTime ? (
                  <p className="text-center omk-text-small text-black-50 mb-1">
                    Resend in {remainingTime}
                  </p>
                ) : (
                  <p
                    className="text-center omk-text-small mb-1"
                    style={{ color: "#28a745", fontWeight: "bold", textDecoration: "underline", cursor: "pointer" }}
                    onClick={resendOTP}
                  >
                    Resend OTP
                  </p>
                )}
              </div>
            </div>
            <div className="d-flex justify-content-center">
              <p className="text-center omk-text-small text-black-50 mb-5">
                <LockIcon style={{ marginRight: "5px", width: "16px", height: "16px" }} />
                Your information is confidential and secure
              </p>
            </div>
            <button
              onClick={handleVerifyOtp}
              className={`btn ${otp.length !== 6 ? "btn-secondary" : "btn-primary"} mb-4 w-50 mobile-full-width`}
              disabled={otp.length !== 6 || loading}
              style={{ borderRadius: "50px", padding: otp.length !== 6 ? "5px 20px" : "10px 20px", height: "auto" }}
            >
              {loading ? "Verifying..." : "Continue"}
            </button>
          </div>
        </Spin>
        <style>{`
            @keyframes shake {
                0% { transform: translateX(0); }
                25% { transform: translateX(-5px); }
                50% { transform: translateX(5px); }
                75% { transform: translateX(-5px); }
                100% { transform: translateX(0); }
            }
            .shake {
                animation: shake 0.5s ease-out;
            }
        `}</style>
      </div>
    </div>
  );
}
